export interface DepthOfFieldSettings {
  enabled: boolean;
  method: 'depth' | 'band';
  blur: number;
  width: number;
  fadeTopDown: boolean;
  focus: 'rider' | 'center';
}

/** Display aids belong to the preview session and never to a render job. */
export interface DepthOfFieldPreview { original: boolean; mask: boolean }

export const DEFAULT_DEPTH_OF_FIELD: Readonly<DepthOfFieldSettings> = {
  enabled: false, method: 'depth', blur: 6, width: .28, fadeTopDown: true, focus: 'rider',
};

export const DEPTH_OF_FIELD_PRESETS = [
  { label: 'Off', values: { enabled: false, blur: 0, width: .18 } },
  { label: 'Subtle', values: { enabled: true, blur: 6, width: .28 } },
  { label: 'Shallow', values: { enabled: true, blur: 12, width: .16 } },
  { label: 'Strong', values: { enabled: true, blur: 22, width: .09 } },
] as const;

/** Omits disabled effects from jobs and legacy request fingerprints. */
export function enabledDepthOfField(settings: DepthOfFieldSettings | undefined): DepthOfFieldSettings | undefined {
  if (!settings?.enabled) return undefined;
  const { method, blur, width, fadeTopDown, focus } = settings;
  return { enabled: true, method, blur, width, fadeTopDown, focus };
}

export function depthOfFieldPreset(settings: DepthOfFieldSettings): string {
  if (!settings.enabled) return 'Off';
  return DEPTH_OF_FIELD_PRESETS.find(preset => preset.values.enabled && preset.values.blur === settings.blur
    && Math.abs(preset.values.width - settings.width) < .000001)?.label ?? 'Custom';
}

/** Numeric focus values ease while method, target and overhead fade change at the waypoint. */
export function interpolateDepthOfField(from: DepthOfFieldSettings, to: DepthOfFieldSettings, amount: number): DepthOfFieldSettings {
  const t = Math.max(0, Math.min(1, amount));
  if (t === 1) return { ...to };
  const startBlur = from.enabled ? from.blur : 0;
  const endBlur = to.enabled ? to.blur : 0;
  return { ...to, enabled: from.enabled || (to.enabled && t > 0),
    blur: startBlur + (endBlur - startBlur) * t, width: from.width + (to.width - from.width) * t };
}

/** Removes blur smoothly between twelve and fifty-two degrees from overhead. */
export function obliqueWeight(pitch: number, fade: boolean): number {
  if (!fade) return 1;
  const t = Math.max(0, Math.min(1, (pitch - 12) / 40));
  return t * t * (3 - 2 * t);
}

export function focusNdcDepth(matrix: ArrayLike<number>, point: { x: number; y: number; z: number }): number {
  const z = matrix[2]! * point.x + matrix[6]! * point.y + matrix[10]! * point.z + matrix[14]!;
  const w = matrix[3]! * point.x + matrix[7]! * point.y + matrix[11]! * point.z + matrix[15]!;
  return z / w;
}
